import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { getFileUrl } from '@/utils/fileUrl';

interface NavItem {
  label: string;
  path: string;
  icon: string;
  roles: string[];
  permissions?: string[];
}

const navigation: NavItem[] = [
  { label: 'Dashboard', path: '/dashboard', icon: 'ri-dashboard-line', roles: ['superadmin', 'company_admin', 'kyc_officer', 'customer'] },
  { label: 'Companies', path: '/companies', icon: 'ri-building-4-line', roles: ['superadmin'] },
  { label: 'Users', path: '/users', icon: 'ri-group-line', roles: ['superadmin', 'company_admin'], permissions: ['users.manage'] },
  { label: 'Customers', path: '/customers', icon: 'ri-user-line', roles: ['superadmin', 'company_admin', 'kyc_officer'], permissions: ['customers.manage', 'customers.view'] },
  { label: 'KYC Requests', path: '/kyc-requests', icon: 'ri-file-list-3-line', roles: ['superadmin', 'company_admin', 'kyc_officer', 'customer'], permissions: ['kyc.manage', 'kyc.review', 'kyc.self'] },
  { label: 'Documents', path: '/documents', icon: 'ri-folder-5-line', roles: ['superadmin', 'company_admin', 'kyc_officer', 'customer'], permissions: ['documents.manage', 'documents.view', 'documents.self'] },
  { label: 'Audit Logs', path: '/audit-logs', icon: 'ri-article-line', roles: ['superadmin', 'company_admin', 'kyc_officer'], permissions: ['audit.view'] },
  { label: 'Reports', path: '/reports', icon: 'ri-bar-chart-box-line', roles: ['superadmin', 'company_admin'], permissions: ['reports.view'] },
  { label: 'Settings', path: '/settings', icon: 'ri-settings-3-line', roles: ['superadmin', 'company_admin'], permissions: ['settings.manage'] },
  { label: 'Masters', path: '/masters', icon: 'ri-database-2-line', roles: ['superadmin'] },
];

const roleLabels: Record<string, string> = {
  superadmin: 'Super Admin',
  company_admin: 'Company Admin',
  kyc_officer: 'KYC Officer',
  customer: 'Customer',
};

export default function Sidebar() {
  const { user, logout, hasRole, hasPermission } = useAuth();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const filteredNav = navigation.filter((item) => {
    if (!hasRole(item.roles)) return false;
    if (item.permissions && !item.permissions.some((p) => hasPermission(p))) return false;
    return true;
  });

  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(`${path}/`);

  const sidebarContent = (
    <>
      {/* Logo */}
      <div className="flex items-center gap-2.5 px-4 py-5">
        <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-brand-600 shrink-0">
          <i className="ri-shield-check-line text-white text-lg" />
        </div>
        {!collapsed && (
          <div className="min-w-0">
            <span className="font-bold text-stone-900 text-sm truncate block">
              {user?.company?.name || 'eKYC Platform'}
            </span>
            <span className="text-stone-400 text-[11px] uppercase tracking-wider">
              {roleLabels[user?.role || '']}
            </span>
          </div>
        )}
      </div>

      {/* Nav Links */}
      <nav className="flex-1 px-3 py-2 overflow-y-auto">
        <ul className="flex flex-col gap-1">
          {filteredNav.map((item) => (
            <li key={item.path}>
              <Link
                to={item.path}
                onClick={() => setMobileOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive(item.path)
                    ? 'bg-brand-50 text-brand-700'
                    : 'text-stone-600 hover:bg-stone-50 hover:text-stone-900'
                }`}
              >
                <div className="w-5 h-5 flex items-center justify-center shrink-0">
                  <i className={`${item.icon} text-base`} />
                </div>
                {!collapsed && <span className="truncate">{item.label}</span>}
              </Link>
            </li>
          ))}
        </ul>
      </nav>

      {/* User Section */}
      <div className="border-t border-stone-100 p-3">
        <div className="flex items-center gap-3 px-2 py-2 rounded-lg">
          <div className="w-9 h-9 rounded-full bg-brand-100 flex items-center justify-center shrink-0 overflow-hidden">
            {user?.avatarUrl ? (
              <img src={getFileUrl(user.avatarUrl)} alt="" className="w-full h-full rounded-full object-cover" />
            ) : (
              <i className="ri-user-line text-brand-600 text-sm" />
            )}
          </div>
          {!collapsed && (
            <div className="min-w-0 flex-1">
              <p className="text-stone-900 text-sm font-medium truncate">
                {user?.fullName}
              </p>
              <p className="text-stone-400 text-xs truncate">{user?.email}</p>
            </div>
          )}
        </div>
        <button
          onClick={logout}
          className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-red-600 hover:bg-red-50 transition-colors w-full mt-1 ${
            collapsed ? 'justify-center' : ''
          }`}
        >
          <div className="w-5 h-5 flex items-center justify-center shrink-0">
            <i className="ri-logout-box-r-line text-base" />
          </div>
          {!collapsed && <span>Sign Out</span>}
        </button>
      </div>
    </>
  );

  return (
    <>
      {/* Mobile Toggle */}
      <button
        onClick={() => setMobileOpen(!mobileOpen)}
        className="fixed top-3 left-3 z-50 md:hidden w-10 h-10 bg-white shadow-md rounded-lg flex items-center justify-center"
        aria-label="Toggle sidebar"
      >
        <i className="ri-menu-line text-stone-700 text-lg" />
      </button>

      {/* Mobile Overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-30 md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed left-0 top-0 bottom-0 z-40 bg-white border-r border-stone-200 flex flex-col transition-all duration-300 ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
        } ${collapsed ? 'w-[72px]' : 'w-[260px]'}`}
      >
        {/* Collapse Toggle (Desktop) */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="hidden md:flex absolute -right-3 top-20 w-6 h-6 bg-white border border-stone-200 rounded-full items-center justify-center shadow-sm z-50"
        >
          <i className={`${collapsed ? 'ri-arrow-right-s-line' : 'ri-arrow-left-s-line'} text-stone-500 text-xs`} />
        </button>

        {sidebarContent}
      </aside>

      {/* Main Content Spacer */}
      <div className={`hidden md:block shrink-0 transition-all duration-300 ${collapsed ? 'w-[72px]' : 'w-[260px]'}`} />
    </>
  );
}